import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { set, get } from 'idb-keyval';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { login as apiLogin, getMe, logout } from '@utils/api';
import { ApiError } from '@utils/api/helpers';
import { useMe } from '@utils/api/patients';
import { AboboCrypto } from '@utils/aboboCrypto';
import { setSentryAuthenticatedUser, clearSentryUser } from '@utils/sentryUser';
import { useSessionHeartbeat } from '@utils/hooks/useSessionHeartbeat';

// The account is named either by the identifier the patient typed or by the
// hint token their message link carried. The password authenticates and derives
// the PHI key in both cases; the token only says which account to ask about.
// Marks a 4xx that an edge service answered rather than the API, so an account
// -specific message is never shown for an infrastructure refusal.
const EDGE_REFUSAL = { base: ['edge-refusal'] };

export type LoginCredentials =
  | { username: string; password: string }
  | { token: string; password: string };

interface AuthContextType {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;

  // Crypto keys
  patientPrivateKey: CryptoKey | null;
  patientPublicKey: CryptoKey | null;
  clientPublicKey: CryptoKey | null;
  keysLoading: boolean;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;

  // Key management
  initializeCryptoKeys: (password: string, userData: any) => Promise<void>;
  clearCryptoKeys: () => Promise<void>;
  // Re-derive the in-memory keys from the password without re-authenticating.
  // Used when the session is valid but the persisted key is unavailable
  // (e.g. refresh in a browser without IndexedDB). See ABOBO-PORTAL-62.
  unlockKeys: (password: string) => Promise<void>;
  // True when authenticated but the private key couldn't be loaded/persisted,
  // so the user must re-enter their password to unlock.
  needsUnlock: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { me, mutate, isLoading: meIsLoading } = useMe();
  const [isLoading, setIsLoading] = useState(false);
  const [keysLoading, setKeysLoading] = useState(true);
  const [patientPrivateKey, setPatientPrivateKey] = useState<CryptoKey | null>(null);
  const [patientPublicKey, setPatientPublicKey] = useState<CryptoKey | null>(null);
  const [clientPublicKey, setClientPublicKey] = useState<CryptoKey | null>(null);
  const navigate = useNavigate();

  const { t } = useTranslation();

  // Send heartbeat to keep session alive when user is active
  useSessionHeartbeat(!!me);

  // Load existing crypto keys from IndexedDB on mount
  useEffect(() => {
    // Auth state not yet determined (initial /patients/me fetch in flight).
    // Keep keysLoading=true so consumers (useTextDecryption) show a loading
    // state instead of concluding the private key is missing. Without this,
    // `me` is briefly undefined on a cold load, we'd flip keysLoading to false
    // before keys load, and every message would flash "decryption failed"
    // (and fire a spurious missing_private_key Sentry event). See ABOBO-PORTAL-61.
    if (meIsLoading) return;

    // If no user, keys aren't loading (nothing to load)
    if (!me) {
      setKeysLoading(false);
      return;
    }

    // If keys are already loaded (e.g., after login), don't reload from storage
    if (patientPrivateKey) {
      setKeysLoading(false);
      return;
    }

    const loadExistingKeys = async () => {
      setKeysLoading(true);
      try {
        const [privateKey, publicKey, clientKey] = await Promise.all([
          get('patientPrivateKey'),
          get('patientPublicKey'),
          get('clientPublicKey')
        ]);

        if (privateKey) setPatientPrivateKey(privateKey);
        if (publicKey) setPatientPublicKey(publicKey);
        if (clientKey) setClientPublicKey(clientKey);
      } catch (error) {
        console.error('Failed to load crypto keys:', error);
      } finally {
        setKeysLoading(false);
      }
    };

    loadExistingKeys();
  }, [me, meIsLoading, patientPrivateKey]);

  // Identify the patient in Sentry on resumed sessions, where handleLogin
  // didn't run this page load and events would otherwise carry only a session id.
  useEffect(() => {
    if (me?.id) {
      setSentryAuthenticatedUser(me.id);
    }
  }, [me?.id]);

  const initializeCryptoKeys = async (
    password: string,
    userData: any,
    context: 'login' | 'unlock' = 'login'
  ) => {
    setKeysLoading(true);
    try {
      const patientDecryptionKey = await AboboCrypto.deriveKey(password, userData.activeKey.salt);

      const [privateKey, publicKey, clientKey] = await Promise.all([
        AboboCrypto.loadClientPrivateKey(userData.activeKey.privateKey, patientDecryptionKey),
        AboboCrypto.importPublicKey(userData.activeKey.publicKey),
        AboboCrypto.importPublicKey(userData.clientPublicKey)
      ]);

      // Update state first — the freshly derived key is what this session
      // needs. A fresh login always re-derives from the password, so IndexedDB
      // is only a refresh cache and must never be able to fail the login.
      setPatientPrivateKey(privateKey);
      setPatientPublicKey(publicKey);
      setClientPublicKey(clientKey);

      // Cache to IndexedDB (best-effort, non-blocking). Some environments
      // (e.g. Chrome on iOS in private/Lockdown mode) don't expose `indexedDB`,
      // and idb-keyval's set() throws *synchronously* on the missing global.
      // That previously escaped the fire-and-forget .catch(), aborted a
      // successful login, and surfaced as a false "Encryption key failure".
      // The try/catch guards the synchronous throw; .catch handles async
      // rejection. See ABOBO-PORTAL-62.
      try {
        Promise.all([
          set('patientPrivateKey', privateKey),
          set('patientPublicKey', publicKey),
          set('clientPublicKey', clientKey)
        ]).catch((error) => console.error('Failed to store crypto keys:', error));
      } catch (error) {
        console.error('Failed to store crypto keys:', error);
      }
    } catch (error) {
      console.error('Failed to initialize crypto keys:', error);
      // Blind spot: this is the source of the "Encryption key failure" login
      // toast, but handleLogin only toasts + rethrows and never reports it.
      // Capture here so failed key derivation/unwrap is visible in Sentry,
      // with enough context to tell malformed-key from insecure-context
      // (e.g. Chrome on iOS / in-app webviews where crypto.subtle is impaired).
      Sentry.captureException(error, {
        tags: { crypto_init_error: true, crypto_init_context: context },
        extra: {
          hasActiveKey: !!userData?.activeKey,
          hasSalt: !!userData?.activeKey?.salt,
          hasPrivateKey: !!userData?.activeKey?.privateKey,
          hasPublicKey: !!userData?.activeKey?.publicKey,
          hasClientPublicKey: !!userData?.clientPublicKey,
          isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
          hasSubtleCrypto: typeof window !== 'undefined' ? !!window.crypto?.subtle : undefined,
        },
      });
      throw error;
    } finally {
      setKeysLoading(false);
    }
  };

  const clearCryptoKeys = async () => {
    try {
      await Promise.all([
        set('patientPrivateKey', null),
        set('patientPublicKey', null),
        set('clientPublicKey', null)
      ]);
      
      setPatientPrivateKey(null);
      setPatientPublicKey(null);
      setClientPublicKey(null);
    } catch (error) {
      console.error('Failed to clear crypto keys:', error);
    }
  };

  // `me` already carries activeKey + clientPublicKey, so this re-derives the
  // same way login does — no re-authentication needed.
  const unlockKeys = async (password: string) => {
    if (!me?.activeKey) {
      throw new Error('Cannot unlock: no active encryption key for this account');
    }
    await initializeCryptoKeys(password, me, 'unlock');
  };

  const handleLogin = async (credentials: LoginCredentials) => {
    setIsLoading(true);

    try {
      const { status, body } = await apiLogin(credentials);

      // A refused login is a 4xx carrying a body, not a rejection, so it has to
      // be read here: left to fall through, the session-less getMe() below
      // turns every refusal into the same opaque 401 and a locked-out account
      // is told its password is wrong.
      if (status >= 400) {
        // A body we could not parse did not come from the API — CloudFront and
        // the WAF answer with HTML — so its status describes an edge refusal
        // rather than this account. That matters at 403, which is both our
        // lockout and the WAF's block, and patients behind one facility's
        // address share its rate limit.
        throw new ApiError(body?.error ?? 'login failed', status, body ? {} : EDGE_REFUSAL);
      }

      const userData = await getMe().then((result) => result.data);

      // Identify the patient before key derivation, so a crypto failure during
      // init is attributed to the patient rather than an anonymous session id.
      if (userData?.id) {
        setSentryAuthenticatedUser(userData.id);
      }

      await initializeCryptoKeys(credentials.password, userData);

      // Refresh user data
      await mutate();

      // Navigation is handled by the Login component

    } catch (error: any) {
      const fromEdge = error?.info === EDGE_REFUSAL;

      if (error.status === 403 && !fromEdge) {
        // Waiting out the lock is the only lossless way back in: a patient
        // password reset rotates the RSA key pair (abobo-api
        // after_reset_password) and leaves every earlier secure message
        // undecryptable, and the API only reveals this 403 to a caller whose
        // password was already correct.
        toast.error(t('secure.login.errors.lockedOutTitle'), {
          description: t('secure.login.errors.lockedOutMessage'),
        });
      } else if (error.status === 401 && !fromEdge) {
        toast.error(t('secure.login.errors.wrongCredentialsTitle'), {
          description: t('secure.login.errors.wrongCredentialsMessage'),
        });
      } else if (error.status) {
        toast.error(t('secure.login.errors.signInFailedTitle'), {
          description: t('secure.login.errors.signInFailedMessage'),
        });
      } else {
        toast.error(t('secure.login.errors.privateKeyErrorTitle'), {
          description: t('secure.login.errors.privateKeyErrorMessage'),
        });
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    clearCryptoKeys();
    clearSentryUser();
    mutate(undefined, {
      revalidate: false,
    });
    setIsLoading(false);
    try {
      await logout();
    } catch {
      // Ignore network errors - 401 means already logged out,
      // other errors are non-critical for logout flow
    }
    navigate({ to: '/secure/login' });
  };

  const needsUnlock = !!me && !keysLoading && !patientPrivateKey;

  // A breadcrumb, not an error: needing unlock is a handled flow (e.g. private
  // mode), so it should create no Sentry issue — only add context if a later
  // error (e.g. a failed unlock) is reported. Once per entry into the state.
  const unlockBreadcrumbRef = useRef(false);
  useEffect(() => {
    if (needsUnlock && !unlockBreadcrumbRef.current) {
      unlockBreadcrumbRef.current = true;
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'Encryption key unavailable — unlock prompt shown',
        data: {
          hasIndexedDB: typeof indexedDB !== 'undefined',
          isSecureContext: typeof window !== 'undefined' ? window.isSecureContext : undefined,
          hasSubtleCrypto: typeof window !== 'undefined' ? !!window.crypto?.subtle : undefined,
        },
      });
    } else if (!needsUnlock) {
      unlockBreadcrumbRef.current = false;
    }
  }, [needsUnlock]);

  const value: AuthContextType = {
    isAuthenticated: !!me,
    isLoading: isLoading || meIsLoading,
    user: me,
    patientPrivateKey,
    patientPublicKey,
    clientPublicKey,
    keysLoading,
    login: handleLogin,
    logout: handleLogout,
    initializeCryptoKeys,
    clearCryptoKeys,
    unlockKeys,
    needsUnlock,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
